/**
 * Approvals analytics utilities
 *
 * Shared constants and helpers for analytics events fired across the Approvals workflow.
 * All approval-related events should use the APPROVALS_ANALYTICS_SOURCE constant as their source.
 */
import type { Approver } from './ApprovalsContext.tsx';

/**
 * Analytics source identifier for all Approvals workflow events.
 * Used as the `source` attribute in UI, Track, and Screen events.
 */
export const APPROVALS_ANALYTICS_SOURCE = 'ConfluenceApprovalsWorkflow';

/**
 * Computes per-status counts from an array of approvers.
 *
 * Returns an object matching the `updatedReviewStatus` / `currentReviewStatus`
 * shape required by the Approvals Analytics Spec.
 */
export const computeReviewStatusCounts = (
	approvers: Approver[],
): { approved: number; declined: number; pending: number; total: number } =>
	approvers.reduce(
		(counts, approver) => {
			if (approver.status === 'REVIEWER_APPROVED') {
				counts.approved++;
			} else if (approver.status === 'REVIEWER_DENIED') {
				counts.declined++;
			} else if (approver.status === 'PENDING_REVIEW') {
				counts.pending++;
			}
			return counts;
		},
		{ approved: 0, declined: 0, pending: 0, total: approvers.length },
	);

/**
 * Shape of a mutation error from the GraphQL response.
 *
 * Mutations return `{ errors: [MutationError!], success: Boolean!, ... }` where
 * each MutationError has optional extensions carrying a statusCode.
 */
type MutationErrorExtension = {
	errorType?: string;
	statusCode?: number;
};

type MutationError = {
	extensions?: MutationErrorExtension[];
	message?: string;
};

/**
 * Extracts the first `statusCode` from a mutation error's extensions.
 *
 * Expects the standard mutation error shape:
 * `{ extensions: [{ errorType, statusCode }], message }`.
 *
 * Returns null when the error does not match the expected shape or contains
 * no statusCode.
 */
export const getFailureStatusCode = (error: unknown): number | null => {
	if (error == null || typeof error !== 'object') {
		return null;
	}

	const mutationError = error as MutationError;

	if (!Array.isArray(mutationError.extensions) || mutationError.extensions.length === 0) {
		return null;
	}

	const statusCode = mutationError.extensions[0]?.statusCode;
	if (typeof statusCode === 'number') {
		return statusCode;
	}

	return null;
};
